import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { Permission, SyncStatusDTO } from "@easyshop/shared";
import { cx } from "@/components/ui";
import { BoutiquePicker } from "@/components/BoutiquePicker";
import { IosChips, MobileChromeContext, MobileIcon, type IconName, type MobileChrome } from "@/components/mobile/MobileUi";
import { useSession } from "@/hooks/useSession";
import { useConversations, useSync } from "@/lib/queries";
import { api } from "@/lib/api";
import { PullToRefresh } from "@/components/mobile/PullToRefresh";
import { useSelectedBoutique } from "@/hooks/useSelectedBoutique";
import { NotificationsMenu } from "./HeaderControls";

/** 5 onglets (recommandation Apple) ; le planning est dans « Plus ». */
const NAV: { to: string; label: string; icon: IconName; perm?: Permission }[] = [
  { to: "/m", label: "Accueil", icon: "home" },
  { to: "/m/stock", label: "Produits", icon: "box", perm: "stock.read" },
  { to: "/m/ventes", label: "Ventes", icon: "ticket", perm: "sales.read" },
  { to: "/m/messages", label: "Messages", icon: "chat", perm: "messages.read" },
  { to: "/m/plus", label: "Plus", icon: "more" },
];

/** Pages de l'onglet « Plus » : l'onglet reste actif quand on y navigue. */
const PLUS_PATHS = ["/m/plus", "/m/planning", "/m/taches", "/m/promos", "/m/arrivages", "/m/acces", "/m/it", "/m/profil"];

/** Titre par défaut de chaque page (une page peut l'imposer avec useMobileTitle). */
const TITLES: [string, string][] = [
  ["/m/stock", "Produits"],
  ["/m/ventes", "Ventes"],
  ["/m/messages", "Messages"],
  ["/m/planning", "Planning"],
  ["/m/taches", "Tâches"],
  ["/m/promos", "Promos"],
  ["/m/arrivages", "Arrivages"],
  ["/m/acces", "Accès & équipe"],
  ["/m/it", "IT"],
  ["/m/profil", "Profil"],
  ["/m/plus", "Plus"],
];

/** Pages sans lien avec la boutique choisie : pas de pastilles boutiques. */
const NO_PICKER = ["/m/plus", "/m/acces", "/m/it", "/m/profil"];

/** Au-delà, les pastilles boutiques laissent la place à une liste avec recherche. */
const MAX_PILLS = 4;

/** Relevé des caisses au « tirer pour actualiser » s'il date de plus de 2 min (la synchro auto passe toutes les 12 min). */
const PULL_SYNC_AFTER_MS = 2 * 60_000;

/**
 * « Tirer pour actualiser » : recharge tout ce qui est affiché ; avec le droit
 * de synchroniser, relève aussi les caisses en arrière-plan si le dernier
 * relevé a plus de 2 min (les chiffres se mettent à jour à la fin du relevé).
 */
function usePullRefresh() {
  const qc = useQueryClient();
  const sync = useSync();
  const { can } = useSession();
  const refresh = async () => {
    const work: Promise<unknown>[] = [qc.refetchQueries({ type: "active" }), new Promise((r) => setTimeout(r, 600))];
    if (can("sync.run") && !sync.isPending) {
      work.push(
        api
          .get<SyncStatusDTO>("/sync")
          .then((s) => {
            const last = s.lastSyncAt ? new Date(s.lastSyncAt).getTime() : 0;
            if (Date.now() - last > PULL_SYNC_AFTER_MS) sync.mutate();
          })
          .catch(() => {})
      );
    }
    await Promise.all(work);
  };
  return { refresh, syncing: sync.isPending };
}

/** Tâches ouvertes / en retard et non-lus de la boutique choisie (fils de conversation déjà chargés). */
export function useBoutiqueActivity() {
  const { selectedId } = useSelectedBoutique();
  const conv = useConversations("tous");
  const all = conv.data ?? [];
  const mine = all.find((c) => c.boutique.id === selectedId);
  return {
    unread: all.reduce((s, c) => s + c.unread, 0),
    unreadHere: mine?.unread ?? 0,
    openTasks: mine?.openTasks ?? 0,
    lateTasks: mine?.lateTasks ?? 0,
    conversations: all,
  };
}

/**
 * Shell mobile (PWA) « style iOS », pour tous les rôles : barre de navigation
 * translucide (retour contextuel, titre de la page, cloche), choix de la boutique
 * (pastilles, ou liste avec recherche au-delà de 4), onglets bas
 * Accueil · Produits · Ventes · Messages · Plus.
 */
export function MobileShell() {
  const { me, can } = useSession();
  const { selectedId, select } = useSelectedBoutique();
  const loc = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { unread, openTasks, lateTasks } = useBoutiqueActivity();
  const pull = usePullRefresh();
  const [chrome, setChrome] = useState<MobileChrome | null>(null);
  const path = loc.pathname.replace(/\/$/, "") || "/m";
  // Sur l'accueil, les cartes des boutiques remplacent le sélecteur.
  const onHome = path === "/m";
  const multiple = me.boutiques.length > 1 && !onHome && !NO_PICKER.some((p) => path.startsWith(p));
  const title = chrome?.title ?? TITLES.find(([p]) => path.startsWith(p))?.[1] ?? "Accueil";
  const homeLogo = onHome && !chrome ? me.organization?.logoUrl : null;
  // L'accueil affiche son grand titre (et son sur-titre) dans la page.
  const subtitle = onHome ? undefined : chrome?.subtitle;
  const inPlus = PLUS_PATHS.some((p) => path.startsWith(p)) && !path.startsWith("/m/plus");
  const back = chrome?.back ?? (inPlus ? { label: "Plus", onClick: () => navigate("/m/plus") } : undefined);

  // Lien vers une boutique (notification, redirection depuis une URL PC) : ?boutique=<id>.
  const boutiqueParam = params.get("boutique");
  useEffect(() => {
    if (!boutiqueParam) return;
    if (me.boutiques.some((b) => b.id === boutiqueParam)) select(boutiqueParam);
    const next = new URLSearchParams(params);
    next.delete("boutique");
    setParams(next, { replace: true });
  }, [boutiqueParam, me.boutiques, params, select, setParams]);

  return (
    <MobileChromeContext.Provider value={setChrome}>
      <div className="ios relative mx-auto min-h-[100dvh] max-w-[480px] overflow-x-clip bg-ios-bg pb-[calc(92px+env(safe-area-inset-bottom))] text-ink">
        <div className="sticky top-0 z-20 border-b-[0.5px] border-ios-sep bg-ios-bar pt-[env(safe-area-inset-top)] backdrop-blur-xl backdrop-saturate-[1.8]">
          {/* Titre centré dans la barre (style iOS), retour à gauche, cloche à droite. */}
          <div className="relative flex h-11 items-center justify-between px-4">
            {back ? (
              <button type="button" onClick={back.onClick} className="relative z-[1] -ml-2 flex min-h-[44px] max-w-[112px] items-center gap-[2px] px-2 text-[17px] text-accent">
                <span aria-hidden className="-mt-[2px] text-[24px] leading-none">
                  ‹
                </span>
                <span className="truncate">{back.label}</span>
              </button>
            ) : (
              <span />
            )}
            <div className="pointer-events-none absolute inset-x-[112px] flex flex-col items-center text-center leading-tight">
              {homeLogo ? (
                // Accueil : le logo du réseau dans la barre, le grand titre est dans la page.
                <div className="flex h-[26px] max-w-full items-center">
                  <img src={homeLogo} alt={me.organization?.name ?? "Accueil"} className="h-full max-w-[150px] object-contain" />
                </div>
              ) : (
                !onHome && <h1 className="max-w-full truncate text-[17px] font-semibold">{title}</h1>
              )}
              {subtitle && <div className="max-w-full truncate text-[12px] text-muted">{subtitle}</div>}
            </div>
            <span className="relative z-[1]">
              <NotificationsMenu />
            </span>
          </div>
        </div>

        <PullToRefresh onRefresh={pull.refresh}>
          {pull.syncing && (
            <div role="status" className="px-4 pt-2 text-center text-[12px] text-muted">
              Relevé des caisses en cours…
            </div>
          )}
          {multiple && (
            <div className="px-4 pt-3">
              {me.boutiques.length <= MAX_PILLS ? (
                <IosChips options={me.boutiques.map((b) => ({ key: b.id, label: b.city }))} value={selectedId ?? ""} onChange={select} />
              ) : (
                <BoutiquePicker boutiques={me.boutiques} value={selectedId ? [selectedId] : []} onChange={(ids) => ids[0] && select(ids[0])} />
              )}
            </div>
          )}

          <div className="flex flex-col gap-[22px] px-4 pt-4">
            <Outlet />
          </div>
        </PullToRefresh>

        {/* Barre d'onglets flottante (capsule translucide) : le contenu défile dessous. */}
        <nav className="fixed inset-x-3 bottom-[max(10px,env(safe-area-inset-bottom))] z-[25] mx-auto flex h-[62px] max-w-[456px] items-center rounded-full border-[0.5px] border-ios-sep bg-ios-bar px-[6px] shadow-[0_8px_30px_rgba(20,30,20,0.16),0_1px_3px_rgba(20,30,20,0.08)] backdrop-blur-2xl backdrop-saturate-[1.8]">
          {NAV.filter((n) => !n.perm || can(n.perm)).map((n) => {
            const active = n.to === "/m" ? onHome : n.to === "/m/plus" ? PLUS_PATHS.some((p) => path.startsWith(p)) : path.startsWith(n.to);
            const badge = n.to === "/m/messages" ? unread : n.to === "/m/plus" && can("messages.read") ? lateTasks || openTasks : 0;
            const color = active ? "var(--accent)" : "oklch(0.5 0.008 150)";
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/m"}
                className={cx(
                  "relative flex h-[50px] min-w-0 flex-1 flex-col items-center justify-center gap-[2px] rounded-full outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent",
                  active && "bg-[oklch(0.4_0.08_155/0.12)]"
                )}
              >
                <MobileIcon name={n.icon} size={24} color={color} strokeWidth={n.icon === "more" ? 3 : 1.9} />
                <div className="max-w-full truncate text-[10px] font-medium tracking-[0.1px]" style={{ color }}>
                  {n.label}
                </div>
                {badge > 0 && (
                  <span className="absolute left-[calc(50%+5px)] top-[2px] flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-ios-badge px-[5px] text-[12px] font-semibold text-white">
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </MobileChromeContext.Provider>
  );
}
